import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, from, Observable, of, Subject } from 'rxjs';
import type { ICountLabelsQuery, IListLabelsQuery, ISaveLabelCommand, ListLabelsLanguage } from '@protocols/use-cases';
import { PaginatedStateEffects } from '@shared/ui';
import { useLocalization } from '@shared/localization';
import { LabelData, LabelState, LabelStateModel } from '../state/label.state';
import { LabelLanguages, SaveLabelLanguage } from '@telespot/domain';

export const LIST_LABELS_QUERY = new InjectionToken<IListLabelsQuery>('LIST_LABELS_QUERY');
export const COUNT_LABELS_QUERY = new InjectionToken<ICountLabelsQuery>('COUNT_LABELS_QUERY');
export const SAVE_LABEL_COMMAND = new InjectionToken<ISaveLabelCommand>('SAVE_LABEL_COMMAND');

@Injectable()
export class LabelEffects extends PaginatedStateEffects<LabelData, LabelStateModel> {
  private readonly localization = useLocalization();
  private readonly saveLabelCommand = inject<ISaveLabelCommand>(SAVE_LABEL_COMMAND);
  private readonly listLabelsQuery = inject<IListLabelsQuery>(LIST_LABELS_QUERY);
  private readonly countLabelsQuery = inject<ICountLabelsQuery>(COUNT_LABELS_QUERY);

  private readonly _saveSucceeded$ = new Subject<string>();
  public readonly saveSucceeded$: Observable<string> = this._saveSucceeded$.asObservable();

  constructor(protected readonly labelState: LabelState) {
    super(labelState);
  }

  override ngOnDestroy(): void {
    this._saveSucceeded$.complete();
    super.ngOnDestroy();
  }

  // ============================================================================
  // Re-exposed selectors from LabelState
  // ============================================================================

  public readonly saving$ = this.labelState.saving$;
  public readonly saveExceptions$ = this.labelState.saveExceptions$;

  public readonly pendingLabels$ = this.labelState.pendingLabels$;
  public readonly labelOverrides$ = this.labelState.labelOverrides$;

  // Backwards-compat aliases
  public readonly labels$ = this.labelState.labels$;
  public readonly nextLabelToken$ = this.labelState.nextLabelToken$;
  public readonly previousLabelTokens$ = this.labelState.previousLabelTokens$;
  public readonly labelsLoaded$ = this.labelState.labelsLoaded$;
  public readonly noLabels$ = this.labelState.noLabels$;

  // ============================================================================
  // Re-exposed updaters from LabelState
  // ============================================================================
  public readonly setSearchTerm = this.labelState.setSearchTerm;
  public readonly changePageSize = this.labelState.changePageSize;
  public readonly addPendingLabel = this.labelState.addPendingLabel;
  public readonly removePendingLabel = this.labelState.removePendingLabel;
  public readonly clearPendingLabels = this.labelState.clearPendingLabels;
  public readonly setLabelOverride = this.labelState.setLabelOverride;
  public readonly removeLabelOverride = this.labelState.removeLabelOverride;
  public readonly clearLabelOverrides = this.labelState.clearLabelOverrides;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly initializeLabels = this.initializeItems;
  public readonly loadMoreLabels = this.loadMoreItems;
  public readonly countLabels = this.countItems;

  // ============================================================================
  // Custom effects
  // ============================================================================

  public readonly saveLabel = this.effect((action$: Observable<{ uuid: string; values: LabelLanguages }>) =>
    action$.pipe(
      tap(() => {
        this.labelState.clearSaveExceptions();
        this.labelState.setSaving(true);
      }),
      switchMap(({ uuid, values }) =>
        this.runSave(uuid, values).pipe(
          tap((savedUuid) => {
            this.labelState.removeLabelOverride(savedUuid);
            this._saveSucceeded$.next(savedUuid);
          }),
          catchError((error: Error) => {
            this.labelState.recordSaveException({ uuid, error });
            this.labelState.setSaving(false);
            return EMPTY;
          })
        )
      )
    )
  );

  /*
   * The list is patched from the command's response, not from the submitted
   * payload: the command whitespace-normalizes what it persists, so the
   * response is the only values map guaranteed to match the database.
   */
  private runSave(uuid: string | undefined, languages: LabelData['languages']): Observable<string> {
    return from(this.saveLabelCommand.execute({ uuid, values: languages })).pipe(
      tap(({ uuid: savedUuid, values }) =>
        this.labelState.updateLabelInList({ id: savedUuid, uuid: savedUuid, languages: values })
      ),
      map(({ uuid: savedUuid }) => savedUuid)
    );
  }

  // ============================================================================
  // Public utility
  // ============================================================================

  public getCurrentLanguage(): string {
    return this.localization.currentLanguage;
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    const snapshot = await combineLatest([this.labelState.pendingLabels$, this.labelState.labelOverrides$])
      .pipe(take(1))
      .toPromise();
    if (!snapshot) return 0;
    const [pendingLabels, labelOverrides] = snapshot;
    const dbCount = await this.countLabelsQuery.execute(searchTerm);
    const pendingCount = pendingLabels.filter((p) => this.matchesSearch(p, searchTerm)).length;
    const overrideDelta = Object.values(labelOverrides).reduce(
      (acc, { original, override }) =>
        acc + (this.matchesSearch(override, searchTerm) ? 1 : 0) - (this.matchesSearch(original, searchTerm) ? 1 : 0),
      0
    );
    return dbCount + pendingCount + overrideDelta;
  }

  /**
   * Delete repository item.
   * The label store does not support deletion.
   * Returning false will invalidate any posterior item removal operations.
   * @param _itemId The ID of the item to remove (not used in this case)
   * @returns Promise that lets you know if the operation was successful, always false in this case
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected listFlow(
    limit: number,
    lastItem: LabelData | undefined,
    searchTerm: string | undefined
  ): Observable<LabelData[]> {
    return combineLatest([this.labelState.pendingLabels$, this.labelState.labelOverrides$]).pipe(
      take(1),
      switchMap(([pendingLabels, labelOverrides]) => {
        const lang = this.getCurrentLanguage();

        const lastItemValue = lastItem?.languages[lang as SaveLabelLanguage];
        const pagination =
          lastItem && lastItemValue !== undefined
            ? { lastItemName: lastItemValue, lastItemUUID: lastItem.uuid }
            : undefined;

        const language = lang as ListLabelsLanguage;
        const cursor = lastItem ? this.cursorKey(lastItem, lang) : undefined;
        const overrideUuids = new Set(Object.keys(labelOverrides));

        const pendingPage = pendingLabels
          .filter((p) => this.matchesSearch(p, searchTerm))
          .filter((p) => cursor === undefined || this.cursorKey(p, lang).localeCompare(cursor) > 0)
          .sort((a, b) => this.compareLabels(a, b, lang));

        const overridesPage = Object.values(labelOverrides)
          .map((entry) => entry.override)
          .filter((o) => this.matchesSearch(o, searchTerm))
          .filter((o) => cursor === undefined || this.cursorKey(o, lang).localeCompare(cursor) > 0)
          .sort((a, b) => this.compareLabels(a, b, lang));

        // Overfetch by |overrides| so that, after dropping stale DB rows whose uuid
        // is locally edited, at least `limit` DB items remain available to merge.
        const dbLimit = limit + overrideUuids.size;

        return from(this.listLabelsQuery.execute({ language, searchTerm, limit: dbLimit, pagination })).pipe(
          map((labelsRecord) => {
            const dbItems: LabelData[] = Object.entries(labelsRecord)
              .filter(([uuid]) => !overrideUuids.has(uuid))
              .map(([uuid, labels]) => ({
                id: uuid,
                uuid,
                languages: labels.reduce<Partial<Record<SaveLabelLanguage, string>>>(
                  (acc, { lang, value }) => ({ ...acc, [lang]: value }),
                  {}
                ) as LabelLanguages,
              }));
            return this.mergeSorted(pendingPage, overridesPage, dbItems, lang, limit);
          })
        );
      })
    );
  }

  /**
   * No counts/metadata are needed to be loaded for the label store.
   * @param _itemId The ID of the item to load counts for
   * @returns Observable resolving to the changes to be applied to the item. Could be only one change or multiple changes over time.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processDeferredAttributes(_itemId: string): Observable<Partial<LabelData>> {
    // No counts/metadata are needed to be loaded for the label store
    return of();
  }

  // ============================================================================
  // Pending-label merge helpers
  // ============================================================================

  /**
   * Cursor key mirrors the repository's: `${value}_${uuid}` (empty value when
   * the user's language is missing). Same-name tie-breaks fall out of the
   * UUID's locale-compare order, identical on both sides of the merge.
   */
  private cursorKey(label: LabelData, lang: string): string {
    const value = label.languages[lang as SaveLabelLanguage] ?? '';
    return `${value}_${label.uuid}`;
  }

  private compareLabels(a: LabelData, b: LabelData, lang: string): number {
    return this.cursorKey(a, lang).localeCompare(this.cursorKey(b, lang));
  }

  private matchesSearch(label: LabelData, searchTerm: string | undefined): boolean {
    if (!searchTerm) return true;
    const needle = searchTerm.toLowerCase();
    // Match the UUID too, mirroring the repository, so locally edited
    // (pending/override) labels surface on a UUID search like saved ones do.
    if (label.uuid?.toLowerCase().includes(needle)) return true;
    return Object.values(label.languages).some((v) => typeof v === 'string' && v.toLowerCase().includes(needle));
  }

  /*
   * 3-way merge of pre-sorted streams. Picks the smallest cursorKey across the
   * three indices at each step. When two streams tie, the order here decides
   * which wins: pending first, then overrides, then DB. The tie-break is
   * cosmetic — the cursor advances by `cursorKey`, so the next page resumes
   * after whichever item was emitted last.
   */
  private mergeSorted(
    pending: LabelData[],
    overrides: LabelData[],
    db: LabelData[],
    lang: string,
    limit: number
  ): LabelData[] {
    const streams: LabelData[][] = [pending, overrides, db];
    const idx = [0, 0, 0];
    const out: LabelData[] = [];
    while (out.length < limit) {
      let pick = -1;
      for (let s = 0; s < streams.length; s++) {
        if (idx[s] >= streams[s].length) continue;
        if (pick === -1 || this.compareLabels(streams[s][idx[s]], streams[pick][idx[pick]], lang) < 0) {
          pick = s;
        }
      }
      if (pick === -1) break;
      out.push(streams[pick][idx[pick]++]);
    }
    return out;
  }
}
